import type { SyntheticEvent } from "react";
import React, { useState, useRef } from "react";
import cn from "classnames";
import { useIsomorphicLayoutEffect } from "@litres-libs/global-hooks";
import type {
  IAdaptiveCoverProps,
  ICoverSizesByHeight,
  ICoverSizesByWidth,
} from "./types";
import { getCoverSrc } from "./utils";
import noCoverIcon from "./assets/noCover.svg";
import styles from "./AdaptiveCover.module.scss";

const defaultSizes: ICoverSizesByWidth = {
  desktopNormal: 200,
  desktopHighDensity: 415,
  mobileNormal: 100,
  mobileHighDensity: 200,
};

export const AdaptiveCover = (props: IAdaptiveCoverProps) => {
  const {
    artId,
    alt,
    imgTagClassName,
    e2eId,
    wrapperClassName,
    coverSizes = defaultSizes,
    onClick,
    getErrorStatus,
    unloaderSize,
    shouldReturnNothingIfError = false,
    ...imgHtmlAttributes
  } = props;
  const imgRef = useRef<HTMLImageElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const src = getCoverSrc(artId, coverSizes);
  const [isError, setIsError] = useState(false);

  const unloaderSizeStyle = unloaderSize
    ? ({ "--unloader-size": `${unloaderSize}px` } as React.CSSProperties)
    : undefined;

  const handleError = () => {
    setIsError(true);

    if (getErrorStatus) {
      getErrorStatus(true);
    }
    if (imgRef.current) {
      imgRef.current.classList.add(styles.errorImg);
    }

    if (wrapperRef.current) {
      wrapperRef.current.classList.add(styles.errorBackground);
      wrapperRef.current.classList.remove(styles.loader);
    }
  };

  const handleSuccessLoad = (e: SyntheticEvent<HTMLImageElement>) => {
    if (wrapperRef.current) {
      wrapperRef.current.classList.remove(styles.loader);
    }
    imgHtmlAttributes.onLoadedData?.(e);
  };

  useIsomorphicLayoutEffect(() => {
    /* если на клиенте динамически поменялся id обложки, то надо сбросить
     * состояние, например на карточке книги при клиентском переходе с одной на другую
     * */
    setIsError(false);

    /* если изображение закешировано браузером, то onLoad не отработает а нам надо удалить класс
     * свойство complete поддерживается всеми браузерами https://developer.mozilla.org/en-US/docs/Web/API/HTMLImageElement/complete
     * */
    if (wrapperRef.current && imgRef.current?.complete === true) {
      wrapperRef.current.classList.remove(styles.loader);
    }

    /* по дефолту у изображения атрибут loading === "eager" это значит что
     * браузер будет грузить изображение до того как произойдет загрузка
     * и выполнение всех скриптов и React в том числе поэтому onError
     * не сработает, т.к. он навесится позже чем прозойдет это событие
     * но при этом свойство complete === true
     * */
    if (
      imgRef.current?.complete === true &&
      imgRef.current?.naturalWidth === 0
    ) {
      handleError();
    }
  }, [artId]);

  if (isError && shouldReturnNothingIfError) {
    return null;
  }

  return (
    <div
      className={cn(styles.container, styles.loader, wrapperClassName)}
      data-testid={e2eId}
      ref={wrapperRef}
      onClick={onClick}
      style={unloaderSizeStyle}
      role="presentation"
    >
      <picture>
        <source
          srcSet={isError ? noCoverIcon : src.srcSetWebpMobile}
          type="image/webp"
          media={`(max-width: ${coverSizes.mobileResponsiveWidth || 699}px)`}
        />
        <source
          srcSet={isError ? noCoverIcon : src.srcSetWebpDesktop}
          type="image/webp"
          media={`(min-width: ${coverSizes.desktopResponsiveWidth || 700}px)`}
        />
        <source
          srcSet={isError ? noCoverIcon : src.srcSetJpgMobile}
          media={`(max-width: ${coverSizes.mobileResponsiveWidth || 699}px)`}
        />
        <source
          srcSet={isError ? noCoverIcon : src.srcSetJpgDesktop}
          media={`(min-width: ${coverSizes.desktopResponsiveWidth || 700}px)`}
        />
        <img
          {...imgHtmlAttributes}
          alt={alt}
          className={cn(styles.image, imgTagClassName)}
          data-testid="adaptiveCover__img"
          onLoad={handleSuccessLoad}
          onError={handleError}
          ref={imgRef}
          src={isError ? noCoverIcon : src.fallbackImage}
          aria-hidden="true"
        />
      </picture>
    </div>
  );
};

/* TODO надо передалать, т.к. компонент Cover не должен знать ни про какие
 * коллекции и если надо что-то кастомизировать, это надо делать по месту использования
 * а не плодить компоненты которые используются в одном месте с разными типами */
interface IAdaptiveCollectionCoverProps {
  alt: string;
  wrapperClassName?: string;
  artIds: number[];
  coverSizes?: ICoverSizesByHeight | ICoverSizesByWidth;
}
export const AdaptiveCollectionCover = ({
  alt,
  artIds,
  wrapperClassName,
  coverSizes,
}: IAdaptiveCollectionCoverProps) => {
  return (
    <div className={cn(styles.collectionCovers, wrapperClassName)}>
      {artIds.slice(0, 3).map((artId, i) => (
        <div
          key={artId}
          className={cn(styles.collectionCovers__child, {
            [styles.collectionCovers__first]: i === 0,
            [styles.collectionCovers__second]: i === 1,
            [styles.collectionCovers__third]: i === 2,
          })}
        >
          <AdaptiveCover
            artId={artId}
            alt={`${alt}-${artId}`}
            loading="lazy"
            decoding="async"
            coverSizes={coverSizes}
          />
        </div>
      ))}
    </div>
  );
};
